<script lang="ts" setup>
import type { FetchError } from 'ofetch'

import BoxberryPickUpPoint from '~/components/cart/BoxberryPickUpPoint.vue'
import LocalPickUpPoint from '~/components/cart/LocalPickUpPoint.vue'
import Order from '~/components/cart/Order.vue'
import Payment from '~/components/cart/Payment.vue'
import Recipient from '~/components/cart/Recipient.vue'
import { usePaymentWidget } from '~/composables/payment-widget'
import OrderPaymentMethod from '~/enums/order-payment-method'
import OrderShippingType from '~/enums/order-shipping-type'
import Page from '~/enums/page'
import helpers from '~/helpers'
import ApiErrorHandler from '~/helpers/api-error-handler'
import { useCartStore } from '~/store/cart'
import type { CreateOrderRequestData } from '~/types/orders'
import type {
  BoxberryPickUpPointShippingData,
  CdekPickupPointShippingData,
  DalliCourierData,
  FivePostPickUpPointShippingData,
  LocalPickUpShippingData,
  RussianPostCourierShippingData,
  RussianPostInternationalParcelShippingData,
  RussianPostPickUpPointShippingData,
  YandexDeliveryPickUpPointShippingData
} from '~/types/shipping'

const messages = {
  // Validation.
  paymentMethodIsNotSelected: 'Способ оплаты не выбран',
  shippingAddressLine1CannotBeEmpty: 'Поле «Название улицы и номер дома» не может быть пустым.',
  shippingAddressLine1ContainsNonLatinLetters: 'В поле «Название улицы и номер дома» должны быть только латинские буквы.',
  shippingAddressLine2ContainsNonLatinLetters: 'В поле «Номер квартиры/офиса» должны быть только латинские буквы.',
  shippingCityCannotBeEmpty: 'Поле «Город / населённый пункт» не может быть пустым.',
  shippingCityContainsNonLatinLetters: 'В поле «Город / населённый пункт» должны быть только латинские буквы.',
  shippingCountryCannotBeEmpty: 'Поле «Страна» не может быть пустым.',
  shippingEmailFieldCannotBeEmpty: 'Поле «Email» не может быть пустым.',
  shippingFirstNameFieldCannotBeEmpty: 'Поле «Имя» не может быть пустым.',
  shippingFirstNameContainsNonLatinLetters: 'В поле «Имя» должны быть только латинские буквы.',
  shippingLastNameFieldCannotBeEmpty: 'Поле «Фамилия» не может быть пустым.',
  shippingLastNameContainsNonLatinLetters: 'В поле «Фамилия» должны быть только латинские буквы.',
  shippingPhoneNumberFieldCannotBeEmpty: 'Поле «Номер телефона» не может быть пустым.',
  shippingPhoneNumberFieldContainsInvalidCharacters: 'Поле «Номер телефона» содержит недопустимые символы.',
  shippingPostcodeCannotBeEmpty: 'Поле «Почтовый индекс» не может быть пустым.',
  shippingPostcodeContainsNonLatinLetters: 'В поле «Почтовый индекс» должны быть только латинские буквы.',
  shippingStateCannotBeEmpty: 'Поле «Регион/штат/провинция» не может быть пустым.',
  shippingStateContainsNonLatinLetters: 'В поле «Регион/штат/провинция» должны быть только латинские буквы.',
  youNeedToAgreeToPreOrder: 'Необходимо согласиться с предзаказом.',
  youNeedToAgreeToTermsAndConditions: 'Необходимо согласиться с правилами.',

  failedToCreateOrder: 'Не удалось создать заказ. Обновите страницу и повторите попытку.',
  orderIsConfirmed: 'Заказ подтверждён.'
}

const router = useRouter()
const { $apiHelper, $toast } = useNuxtApp()
const cartStore = useCartStore()
const { initCloudPaymentsWidget, trackOrder } = usePaymentWidget()

watch(() => cartStore.orderShippingType, updateForm, { immediate: true })
watch(() => cartStore.shippingData, updateForm, { immediate: true })

useHead(() => {
  if (cartStore.orderPaymentMethod === OrderPaymentMethod.CloudPayments) {
    return {
      script: [{ src: 'https://widget.cloudpayments.ru/bundles/cloudpayments.js' }]
    }
  }

  return {}
});

function fillForm (): void {
  const {
    addressLine1, addressLine2, city, country, email, firstName, lastName, phoneNumber, postcode, state
  } = cartStore.userShippingData ?? {}

  cartStore.form.shippingAddressLine1 = addressLine1 ?? null
  cartStore.form.shippingAddressLine2 = addressLine2 ?? null
  cartStore.form.shippingCity = city ?? null
  cartStore.form.shippingCountry = country ?? null
  cartStore.form.shippingEmail = email ?? null
  cartStore.form.shippingFirstName = firstName ?? null
  cartStore.form.shippingLastName = lastName ?? null
  cartStore.form.shippingPhoneNumber = phoneNumber ?? null
  cartStore.form.shippingPostcode = postcode ?? null
  cartStore.form.shippingState = state ?? null
}

async function payForOrder (): Promise<void> {
  const {
    comment,
    shippingAddressLine1,
    shippingAddressLine2,
    shippingCity,
    shippingCountry,
    shippingEmail,
    shippingFirstName,
    shippingLastName,
    shippingPhoneNumber,
    shippingPostcode,
    shippingState
  } = cartStore.form
  const {
    paymentMethodIsNotSelected,
    shippingAddressLine1CannotBeEmpty,
    shippingAddressLine1ContainsNonLatinLetters,
    shippingAddressLine2ContainsNonLatinLetters,
    shippingCityCannotBeEmpty,
    shippingCityContainsNonLatinLetters,
    shippingCountryCannotBeEmpty,
    shippingEmailFieldCannotBeEmpty,
    shippingFirstNameFieldCannotBeEmpty,
    shippingFirstNameContainsNonLatinLetters,
    shippingLastNameFieldCannotBeEmpty,
    shippingLastNameContainsNonLatinLetters,
    shippingPhoneNumberFieldCannotBeEmpty,
    shippingPhoneNumberFieldContainsInvalidCharacters,
    shippingPostcodeCannotBeEmpty,
    shippingPostcodeContainsNonLatinLetters,
    shippingStateCannotBeEmpty,
    shippingStateContainsNonLatinLetters,
    youNeedToAgreeToPreOrder,
    youNeedToAgreeToTermsAndConditions,

    failedToCreateOrder,
    orderIsConfirmed
  } = messages

  const errors: { condition: boolean, message: string }[] = [
    { condition: !shippingFirstName, message: shippingFirstNameFieldCannotBeEmpty },
    { condition: !shippingLastName, message: shippingLastNameFieldCannotBeEmpty },
    { condition: !shippingEmail, message: shippingEmailFieldCannotBeEmpty },
    { condition: !shippingPhoneNumber, message: shippingPhoneNumberFieldCannotBeEmpty },
    {
      condition: !/^[\d\s\-+()]*$/.test(String(shippingPhoneNumber)),
      message: shippingPhoneNumberFieldContainsInvalidCharacters
    }
  ]

  const {
    BoxberryCourier,
    BoxberryPickUpPoint,
    CdekPickupPoint,
    DalliCourier,
    DostavistaInsideMkad,
    DostavistaOutsideMkad,
    FivePostPickUpPoint,
    LocalPickUp,
    RussianPostCourier,
    RussianPostInternationalParcel,
    RussianPostPickUpPoint,
    YandexDeliveryPickUpPoint
  } = OrderShippingType

  if (
    cartStore.orderShippingType !== null
    && [BoxberryCourier, DostavistaInsideMkad, DostavistaOutsideMkad].includes(cartStore.orderShippingType)
  ) {
    errors.push(...[
      { condition: !shippingPostcode, message: shippingPostcodeCannotBeEmpty },
      { condition: !shippingCountry, message: shippingCountryCannotBeEmpty },
      { condition: !shippingState, message: shippingStateCannotBeEmpty },
      { condition: !shippingCity, message: shippingCityCannotBeEmpty },
      { condition: !shippingAddressLine1, message: shippingAddressLine1CannotBeEmpty }
    ])
  } else if (cartStore.orderShippingType === RussianPostInternationalParcel) {
    // eslint-disable-next-line no-control-regex
    const regularExpression = /^[\x00-\x7F]*$/
    errors.push(...[
      { condition: !shippingPostcode, message: shippingPostcodeCannotBeEmpty },
      { condition: !shippingCity, message: shippingCityCannotBeEmpty },
      { condition: !shippingAddressLine1, message: shippingAddressLine1CannotBeEmpty },
      { condition: !regularExpression.test(String(shippingFirstName)), message: shippingFirstNameContainsNonLatinLetters },
      { condition: !regularExpression.test(String(shippingLastName)), message: shippingLastNameContainsNonLatinLetters },
      { condition: !regularExpression.test(String(shippingPostcode)), message: shippingPostcodeContainsNonLatinLetters },
      { condition: !regularExpression.test(String(shippingState)), message: shippingStateContainsNonLatinLetters },
      { condition: !regularExpression.test(String(shippingCity)), message: shippingCityContainsNonLatinLetters },
      { condition: !regularExpression.test(String(shippingAddressLine1)), message: shippingAddressLine1ContainsNonLatinLetters },
      { condition: !regularExpression.test(String(shippingAddressLine2)), message: shippingAddressLine2ContainsNonLatinLetters }
    ])
  }

  errors.push(...[
    { condition: cartStore.orderPaymentMethod === null, message: paymentMethodIsNotSelected },
    { condition: !cartStore.form.isAgreeToTermsAndConditions, message: youNeedToAgreeToTermsAndConditions },
    {
      condition: cartStore.preOrderCartProductsData.length > 0 && !cartStore.form.isAgreeToPreOrder,
      message: youNeedToAgreeToPreOrder
    }
  ])

  for (const { condition, message } of errors) {
    if (condition) {
      $toast.error(message)

      return
    }
  }

  const data: CreateOrderRequestData = {
    comment,
    paymentMethodId: cartStore.orderPaymentMethod!,
    shippingCostInKopecks: cartStore.finalShippingCostInKopecks ?? 0,
    shippingEmail: shippingEmail!,
    shippingFirstName: shippingFirstName!,
    shippingLastName: shippingLastName!,
    shippingPhoneNumber: shippingPhoneNumber!,
    shippingTypeId: cartStore.orderShippingType!
  }

  if (cartStore.orderPaymentMethod === OrderPaymentMethod.CloudPayments) {
    data.useCloudPaymentsWidgetData = 'yes'
  }

  switch (cartStore.orderShippingType) {
    case BoxberryPickUpPoint:
      data.boxberryPickUpPointCode = (cartStore.shippingData as BoxberryPickUpPointShippingData).code
      break
    case CdekPickupPoint: {
      const { pickupPointId } = cartStore.shippingData as CdekPickupPointShippingData
      data.shippingData = { pickupPointId }
      break
    }
    case DalliCourier: {
      const { dalliAddress, dalliDate, dalliMinTime, dalliMaxTime, dalliServiceCode, dalliZone }
        = cartStore.shippingData as DalliCourierData

      data.shippingData = {
        dalliAddress,
        dalliDate,
        dalliMinTime,
        dalliMaxTime,
        dalliServiceCode,
        dalliZone
      }
      break
    }
    case DostavistaInsideMkad:
    case DostavistaOutsideMkad:
      data.shippingAddressLine1 = shippingAddressLine1
      data.shippingAddressLine2 = shippingAddressLine2
      data.shippingCity = shippingCity
      data.shippingCountry = shippingCountry
      data.shippingPostcode = shippingPostcode
      data.shippingState = shippingState
      break
    case FivePostPickUpPoint: {
      const { pickUpPointId } = cartStore.shippingData as FivePostPickUpPointShippingData
      data.shippingData = { pickUpPointId }
      break
    }
    case LocalPickUp: {
      const { pickUpPointId } = cartStore.shippingData as LocalPickUpShippingData
      data.shippingData = { pickUpPointId }
      break
    }
    case RussianPostCourier: {
      const { address, area, comment, details, mailType, place, postcode, region }
        = cartStore.shippingData as RussianPostCourierShippingData
      data.russianPostAddressAddress = address
      data.russianPostAddressArea = area
      data.russianPostAddressComment = comment
      data.russianPostAddressDetails = details
      data.russianPostAddressMailType = mailType
      data.russianPostAddressPlace = place
      data.russianPostAddressPostcode = postcode
      data.russianPostAddressRegion = region
      break
    }
    case RussianPostInternationalParcel: {
      const { countryId, countryName } = cartStore.shippingData as RussianPostInternationalParcelShippingData
      data.shippingAddressLine1 = shippingAddressLine1
      data.shippingAddressLine2 = shippingAddressLine2
      data.shippingCity = shippingCity
      data.shippingData = { countryId }
      data.shippingCountry = countryName
      data.shippingPostcode = shippingPostcode
      data.shippingState = shippingState
      break
    }
    case RussianPostPickUpPoint: {
      const { address, area, boxSize, details, mailType, place, postcode, region, type }
        = cartStore.shippingData as RussianPostPickUpPointShippingData
      data.russianPostPickUpPointAddress = address
      data.russianPostPickUpPointArea = area
      data.russianPostPickUpPointBoxSize = boxSize
      data.russianPostPickUpPointDetails = details
      data.russianPostPickUpPointMailType = mailType
      data.russianPostPickUpPointPlace = place
      data.russianPostPickUpPointPostcode = postcode
      data.russianPostPickUpPointRegion = region
      data.russianPostPickUpPointType = type
      break
    }
    case YandexDeliveryPickUpPoint: {
      const { pickUpPointId } = cartStore.shippingData as YandexDeliveryPickUpPointShippingData
      data.shippingData = { pickUpPointId }
      break
    }
    default:
      break
  }

  if (cartStore.isPromoCodeApplied && cartStore.form.promoCodeName) {
    data.promoCodeName = cartStore.form.promoCodeName
  }

  if (cartStore.arePointsApplied && cartStore.form.pointsToSpend !== null && cartStore.form.pointsToSpend > 0) {
    data.pointsToSpend = cartStore.form.pointsToSpend
  }

  if (
    cartStore.areSuperminsPointsApplied
    && cartStore.form.superminsPointsToSpend !== null
    && cartStore.form.superminsPointsToSpend > 0
  ) {
    data.superminsPointsToSpend = cartStore.form.superminsPointsToSpend
  }

  if (cartStore.isCertificateApplied && cartStore.form.certificateName) {
    data.certificateName = cartStore.form.certificateName
  }

  const utmParameters = helpers.getUtmParameters()

  if (utmParameters) {
    const admitadUid = useCookie('admitad_uid').value

    if (typeof admitadUid === 'string') {
      utmParameters.admitadUid = admitadUid
    }

    const cityAdsClickId = useCookie('cityads_click_id').value

    if (typeof cityAdsClickId === 'string') {
      utmParameters.cityAdsClickId = cityAdsClickId
    }

    data.utmParameters = utmParameters
  }

  if (cartStore.isBloggerOrder) {
    data.isBloggerOrder = 'yes'
  }

  if (cartStore.isRepeatOrder) {
    data.isRepeatOrder = 'yes'
  }

  if (cartStore.isUrgentOrder) {
    data.isUrgentOrder = 'yes'
  }

  let orderData = null

  cartStore.isOrderCreating = true

  try {
    const response = await $apiHelper.orders.createOrder(data)

    orderData = response.data

    $toast.success(orderIsConfirmed)
  } catch (error) {
    ApiErrorHandler.handleFetchError(error as FetchError, failedToCreateOrder)
  }

  if (orderData) {
    const { id: orderId, meta } = orderData
    const { paymentUrl } = meta

    if (
      cartStore.orderPaymentMethod === OrderPaymentMethod.CloudPayments
      && meta.cloudPaymentsWidgetData
    ) {
      initCloudPaymentsWidget(meta.cloudPaymentsWidgetData, orderId)
    } else {
      trackOrder(orderId)

      // Wait 2 seconds before redirecting to have time to track the order.
      setTimeout(
        async () => {
          if (paymentUrl) {
            window.location.replace(paymentUrl)
          } else {
            await router.push(Page.AccountOrders)
          }

          cartStore.isOrderCreating = false
        },
        2000
      )
    }
  } else {
    cartStore.isOrderCreating = false
  }
}

function updateForm (): void {
  if (cartStore.userShippingData) {
    fillForm()
  }

  const { DostavistaInsideMkad, DostavistaOutsideMkad, RussianPostInternationalParcel } = OrderShippingType

  switch (cartStore.orderShippingType) {
    case DostavistaInsideMkad:
      cartStore.form.shippingCity = 'Москва'
      cartStore.form.shippingCountry = 'Россия'
      cartStore.form.shippingState = 'г. Москва'
      break
    case DostavistaOutsideMkad:
      cartStore.form.shippingCountry = 'Россия'
      break
    case RussianPostInternationalParcel:
      if (cartStore.shippingData) {
        cartStore.form.shippingCountry = (cartStore.shippingData as RussianPostInternationalParcelShippingData).countryName
      }

      break
  }
}
</script>

<template>
  <div>
    <div class="row">
      <div class="column-6 tb12 with-margin-bottom-24">
        <Recipient />

        <template v-if="cartStore.shippingData">
          <LocalPickUpPoint v-if="cartStore.orderShippingType === OrderShippingType.LocalPickUp" class="mt-6" />
          <BoxberryPickUpPoint
            v-else-if="cartStore.orderShippingType === OrderShippingType.BoxberryPickUpPoint"
            class="mt-6"
          />
        </template>
      </div>
      <div class="column-6 tb12">
        <Order class="mb-6" />

        <Payment @pay-for-order="payForOrder" />
      </div>
    </div>
  </div>
</template>
